import React from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";

// Glyph per severity. Colour is intentionally NOT derived from severity:
// every dialog uses the same neutral chip so the UI stays quiet and on-palette.
const severityIcons = {
  success: "check_circle",
  error: "error_outline",
  warning: "warning_amber",
  info: "info",
  question: "help_outline",
};

/**
 * Shared shell for the app's message dialogs (AlertDialog / ConfirmDialog).
 * Keeps padding, radius, typography and icon treatment identical everywhere.
 */
function BaseDialog({
  open,
  onClose,
  idPrefix,
  icon,
  title,
  message,
  children,
  actions,
  maxWidth,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={`${idPrefix}-title`}
      aria-describedby={`${idPrefix}-description`}
      PaperProps={{
        sx: (theme) => ({
          borderRadius: theme.borders.borderRadius.xl,
          boxShadow: theme.boxShadows.xxl,
          minWidth: { xs: 0, sm: 420 },
          maxWidth: maxWidth || "calc(100vw - 64px)",
          overflow: "hidden",
        }),
      }}
    >
      <DialogTitle
        id={`${idPrefix}-title`}
        sx={(theme) => ({
          // Borderless header: the spacing below carries the separation.
          border: "none",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 3, sm: 3.5 },
          pt: 3,
          pb: 0,
          fontSize: theme.typography.size.lg,
          fontWeight: theme.typography.fontWeightBold,
          lineHeight: 1.4,
        })}
      >
        {icon && (
          <MDBox
            aria-hidden="true"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            width={36}
            height={36}
            borderRadius="lg"
            sx={(theme) => ({
              // Neutral chip (no accent tint) — same treatment as DialogHeader.
              backgroundColor: theme.palette.grey[200],
              color: theme.palette.text.focus,
            })}
          >
            <Icon fontSize="small">{icon}</Icon>
          </MDBox>
        )}
        {title}
      </DialogTitle>

      <DialogContent
        sx={{
          // Vertical padding-top comes from the theme's shared
          // `DialogTitle + DialogContent` rule, so every dialog matches.
          px: { xs: 3, sm: 3.5 },
          pb: 1,
        }}
      >
        {message != null && (
          <DialogContentText
            id={`${idPrefix}-description`}
            sx={(theme) => ({
              m: 0,
              maxWidth: "72ch",
              fontSize: theme.typography.size.md,
              lineHeight: 1.6,
              color: theme.palette.text.secondary,
              whiteSpace: "pre-line",
              overflowWrap: "anywhere",
            })}
          >
            {message}
          </DialogContentText>
        )}
        {children}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 3, sm: 3.5 },
          pt: 1,
          pb: 3,
          gap: 1,
        }}
      >
        {actions}
      </DialogActions>
    </Dialog>
  );
}

BaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  idPrefix: PropTypes.string.isRequired,
  icon: PropTypes.string,
  title: PropTypes.node.isRequired,
  message: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BaseDialog.defaultProps = {
  onClose: undefined,
  icon: null,
  message: null,
  children: null,
  maxWidth: null,
};

export { severityIcons };
export default BaseDialog;
